import React from 'react';
import {connect} from 'react-redux';
import {injectIntl, defineMessages} from 'react-intl';
import {formatCurrency} from '../../../../utils/formatters';
import {MONTHS} from '../../../../utils/destination-graph';
import {PriceBar} from './PriceBar';

export const messages = defineMessages({
    heading: {
        id: 'module.destination.infographic.pricing.heading',
        defaultMessage: 'UNTRANSLATED'
    },
    subheading: {
        id: 'module.destination.infographic.pricing.subheading',
        defaultMessage: 'UNTRANSLATED'
    },
    monthLabels: {
        ...MONTHS.reduce((monthMessages, month) => {
            monthMessages[month] = {
                id: `module.destination.infographic.pricing.${month}.mmm`,
                defaultMessage: 'UNTRANSLATED'
            };
            return monthMessages;
        }, {})
    }
});

const mapStateToProps = (state) => ({
    simpleDestinationName: state.destination.details.simpleName,
    infographic: state.destination.infographic,
    isMobile: state.config.deviceType === 'mobile'
});

export class PriceGraphComponent extends React.Component {
    static VERTICAL_AXIS_INTERVALS = [100, 250, 500, 1000, 2000, 5000];
    static VERTICAL_AXIS_TICKS = 4;

    constructor() {
        super();
    }

    renderColumn(label, price) {
        const {
            intl: {formatMessage, formatNumber},
            infographic: {currency}
        } = this.props;

        const {domainMinimum, domainRange} = this.verticalAxisInterval();
        const ratio = (price - domainMinimum) / domainRange;
        const fillPercentage = `${Math.round(ratio * 100)}%`;

        const monthPrice = formatCurrency(formatNumber, price, currency);
        const monthLabel = formatMessage(messages.monthLabels[label]);

        return (
            <PriceBar
                key={`price-bar-${label}`}
                {...{label, fillPercentage, monthPrice, monthLabel}}
            />
        );
    }

    renderColumns() {
        const {infographic} = this.props;

        return MONTHS.map((month) => {
            const price = infographic.prices[month];
            return this.renderColumn(month, price);
        });
    }

    verticalAxisInterval() {
        // Cache the results of this calculation because we don't
        // update the prices multiple times in the same request lifecycle
        if (this.selectedInterval) {
            return this.selectedInterval;
        }

        const {
            VERTICAL_AXIS_INTERVALS,
            VERTICAL_AXIS_TICKS
        } = PriceGraphComponent;

        const {infographic: {prices: priceMap}} = this.props;
        const prices = Object.keys(priceMap).map((key) => priceMap[key]);
        const minValue = Math.min(...prices);
        const maxValue = Math.max(...prices);

        const intervals = VERTICAL_AXIS_INTERVALS.map((interval) => {
            const offsetMinValue = minValue - interval;
            const offsetMinimum =
                Math.floor(offsetMinValue / interval) * interval;

            const domainMinimum = offsetMinimum >= 0 ? offsetMinimum : 0;
            const domainMaximum = Math.ceil(maxValue / interval) * interval;
            const domainRange = domainMaximum - domainMinimum;

            // Add one to the number of ticks because there should be
            // a tick at the top and another at the bottom of the axis
            const numberOfTicks = Math.floor(domainRange / interval + 1);

            // These are all the properties returned from this function
            return {
                interval,
                domainRange,
                domainMinimum,
                domainMaximum,
                numberOfTicks
            };
        });

        // Select the smallest possible interval that allows to have
        // a minimum of VERTICAL_AXIS_TICKS ticks
        const selectedInterval = intervals.reduce(
            (smallestInterval, interval) => {
                if (interval.numberOfTicks >= VERTICAL_AXIS_TICKS) {
                    return interval;
                }
                return smallestInterval;
            }
        );

        // Cache the selected smallest interval
        this.selectedInterval = selectedInterval;
        return selectedInterval;
    }

    verticalAxisTicks() {
        const {
            numberOfTicks,
            interval,
            domainMinimum
        } = this.verticalAxisInterval();

        const range = (n) => [...Array(n).keys()]; // 0, 1, 2
        const reverseRange = (n) => range(n).sort((a, b) => b - a); // 2, 1, 0

        return reverseRange(numberOfTicks).map(
            (tick) => domainMinimum + tick * interval
        );
    }

    renderVerticalAxis() {
        const {intl: {formatNumber}, infographic: {currency}} = this.props;

        return (
            <div className="PriceGraph__graph__verticalAxis">
                {this.verticalAxisTicks().map((tick) => (
                    <div
                        className="PriceGraph__graph__verticalAxis__item"
                        key={tick}
                    >
                        {formatCurrency(formatNumber, tick, currency)}
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const {intl: {formatMessage}} = this.props;

        return (
            <div className="row PriceGraph">
                <div className="col-xs-12">
                    <h3>{formatMessage(messages.heading)}</h3>
                    <p>{formatMessage(messages.subheading)}</p>
                </div>
                <div className="col-xs-12 PriceGraph__graph">
                    {this.renderVerticalAxis()}

                    <div className="PriceGraph__graph__body">
                        {this.renderColumns()}
                    </div>
                </div>
            </div>
        );
    }
}

export const PriceGraph = injectIntl(
    connect(mapStateToProps)(PriceGraphComponent)
);
